/**
 * Utility functions for detecting and parsing embed URLs from text
 */

export interface EmbedInfo {
  type: 'youtube' | 'spotify';
  embedUrl: string;
  originalUrl: string;
}

/**
 * Extracts YouTube video ID from various YouTube URL formats
 */
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      // YouTube IDs are typically 11 characters, but we'll accept 6-15 to be flexible
      const id = match[1];
      if (id.length >= 6 && id.length <= 15) {
        return id;
      }
    }
  }

  return null;
}

/**
 * Extracts Spotify episode/track/show ID from Spotify URLs
 */
function extractSpotifyId(url: string): { type: string; id: string } | null {
  const pattern = /open\.spotify\.com\/(episode|track|show|playlist|album)\/([a-zA-Z0-9]+)/;
  const match = url.match(pattern);
  
  if (match && match[1] && match[2]) {
    return { type: match[1], id: match[2] };
  }

  return null;
}

/**
 * Detects and extracts embed information from text
 * Returns up to maxEmbeds embeds (default: 2)
 */
export function detectEmbeds(text: string, maxEmbeds: number = 2): EmbedInfo[] {
  const embeds: EmbedInfo[] = [];
  
  // URL pattern to find all URLs in text (handles URLs that might have trailing punctuation/whitespace)
  const urlPattern = /(https?:\/\/[^\s\)]+)/g;
  const matches = text.matchAll(urlPattern);

  for (const match of matches) {
    if (embeds.length >= maxEmbeds) break;
    
    const url = match[0].replace(/[,;:.!?]+$/, ''); // Remove trailing punctuation

    // Check for YouTube
    const youtubeId = extractYouTubeId(url);
    if (youtubeId) {
      embeds.push({
        type: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
        originalUrl: url,
      });
      continue;
    }

    // Check for Spotify
    const spotifyInfo = extractSpotifyId(url);
    if (spotifyInfo) {
      embeds.push({
        type: 'spotify',
        embedUrl: `https://open.spotify.com/embed/${spotifyInfo.type}/${spotifyInfo.id}`,
        originalUrl: url,
      });
      continue;
    }
  }

  return embeds;
}

/**
 * Removes embed URLs from text
 */
export function removeEmbedUrls(text: string, embeds: EmbedInfo[]): string {
  let cleanedText = text;
  
  for (const embed of embeds) {
    cleanedText = cleanedText.replace(embed.originalUrl, '').trim();
  }
  
  return cleanedText;
}
